import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Calendar,
  Clock,
  User,
  FileSignature,
  FileText,
  Loader2,
  Pencil,
  Trash2,
  ListChecks,
  Lock,
  Eye,
  CalendarPlus,
  Download,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2,
  DollarSign,
  BarChart3,
} from "lucide-react";
import Button from "../../components/shared/Button";
import Input from "../../components/shared/Input";
import CustomSelect from "../../components/shared/CustomSelect";
import DataTable from "../../components/shared/DataTable";
import ChannelBadge from "../../components/shared/ChannelBadge";
import { DatePicker } from "../../components/client";
import { useToastContext } from "../../contexts/ToastContext";
import { useMyServices } from "../../services/vendorServiceService";
import { useMyServicePackages } from "../../services/vendorServicePackageService";
import {
  useVendorPrivateBookings,
  useCreateVendorPrivateBooking,
  useUpdateVendorPrivateBooking,
  useDeleteVendorPrivateBooking,
  useImportVendorPrivateBookings,
  vendorPrivateBookingApi,
} from "../../services/vendorPrivateBookingService";
import { formatDate, formatTime } from "../../utils/dateUtils";
import {
  VendorPageHeader,
  VendorGuideCallout,
} from "../../components/vendor/guidance";
import { VENDOR_PAGE_GUIDES } from "../../utils/vendorPageGuides";

const STATUS_OPTIONS = [
  { value: "confirmed", label: "Confirmed" },
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const parseApiFieldErrors = (err) => {
  const errors = err?.response?.data?.errors;
  if (!errors || typeof errors !== "object") return {};
  const fieldErrors = {};
  Object.entries(errors).forEach(([key, messages]) => {
    const msg = Array.isArray(messages) ? messages[0] : messages;
    if (msg) fieldErrors[key] = String(msg);
  });
  return fieldErrors;
};

const getApiErrorMessage = (err, fallback, fieldErrors = {}) => {
  const first = Object.values(fieldErrors)[0];
  if (first) return first;
  return err?.response?.data?.message || fallback;
};

const resolveDiscountAmount = (subtotal, discountType, discountValue) => {
  const sub = Number(subtotal) || 0;
  const value = parseFloat(discountValue);
  if (!discountType || Number.isNaN(value) || value <= 0) return 0;
  if (discountType === "percent") {
    return Math.min(
      sub,
      Math.round(sub * (Math.min(100, value) / 100) * 100) / 100,
    );
  }
  return Math.min(sub, Math.round(value * 100) / 100);
};

const emptyForm = () => ({
  guest_name: "",
  guest_email: "",
  guest_phone: "",
  manual_title: "",
  use_catalog: true,
  service_id: "",
  package_id: "",
  custom_service_name: "",
  event_date: "",
  start_time: "10:00",
  end_time: "12:00",
  duration_hours: "",
  guest_count: "",
  event_type: "Private",
  event_location: "",
  event_address: "",
  special_requirements: "",
  vendor_notes: "",
  subtotal_amount: "",
  discount_type: "",
  discount_value: "",
  amount_paid: "",
  status: "confirmed",
});

const getStatusBadge = (status) => {
  const styles = {
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    confirmed: "bg-emerald-50 text-emerald-800 border-emerald-200",
    in_progress: "bg-surface-muted text-ink border-border",
    completed: "bg-gray-100 text-gray-700 border-gray-200",
    cancelled: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium capitalize border ${styles[status] || styles.completed}`}
    >
      {status?.replace("_", " ")}
    </span>
  );
};

const privateBookingNetAmount = (booking) => {
  const gross = Number(booking?.subtotal_amount) || 0;
  const discount = Number(booking?.discount_amount) || 0;
  const payout = Number(booking?.vendor_payout_amount);
  if (
    Number.isFinite(payout) &&
    !(discount > 0 && Math.abs(gross - payout) < 0.021)
  ) {
    return payout;
  }
  return Math.max(0, gross - discount);
};

const VendorPrivateBookings = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToastContext();
  const [activeTab, setActiveTab] = useState("list");
  const [statusFilter, setStatusFilter] = useState("");
  const [form, setForm] = useState(emptyForm());
  const [fieldErrors, setFieldErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const fileInputRef = useRef(null);

  const listParams = useMemo(
    () => (statusFilter ? { status: statusFilter } : {}),
    [statusFilter],
  );
  const { data: listRes, isLoading } = useVendorPrivateBookings(listParams);
  const bookings = listRes?.data?.data || [];
  const stats = listRes?.statistics || {};
  const upcomingCount =
    (stats.pending_bookings || 0) +
    (stats.confirmed_bookings || 0) +
    (stats.in_progress_bookings || 0);

  const { data: servicesRes } = useMyServices({ is_active: 1 });
  const services = servicesRes?.data?.data || servicesRes?.data || [];

  const { data: packagesRes } = useMyServicePackages(
    { service_id: form.service_id, per_page: 100 },
    { enabled: form.use_catalog && !!form.service_id },
  );
  const allPackages = useMemo(() => {
    const raw = packagesRes?.data?.data ?? packagesRes?.data ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [packagesRes]);

  const serviceOptions = useMemo(
    () => services.map((s) => ({ value: s.id, label: s.service_name })),
    [services],
  );

  const packageOptions = useMemo(() => {
    if (!form.service_id) return [];
    return allPackages
      .filter((p) => p.service_id === form.service_id)
      .map((p) => ({
        value: p.id,
        label: `${p.package_name} — $${Number(p.base_price || 0).toFixed(2)}`,
      }));
  }, [allPackages, form.service_id]);

  const createMutation = useCreateVendorPrivateBooking({
    onSuccess: () => {
      success(
        "Booking created",
        "Private booking created. It will appear on your calendar.",
      );
      setEditingId(null);
      setForm(emptyForm());
      setFieldErrors({});
      setActiveTab("list");
    },
    onError: (err) => {
      const parsed = parseApiFieldErrors(err);
      if (!form.use_catalog && parsed.service_id) {
        parsed.custom_service_name =
          parsed.custom_service_name || parsed.service_id;
        delete parsed.service_id;
      }
      setFieldErrors(parsed);
      showError(
        "Could not create booking",
        getApiErrorMessage(err, "Please check the form.", parsed),
      );
    },
  });

  const updateMutation = useUpdateVendorPrivateBooking({
    onSuccess: () => {
      success("Booking updated", "Private booking saved successfully.");
      setEditingId(null);
      setForm(emptyForm());
      setFieldErrors({});
      setActiveTab("list");
    },
    onError: (err) => {
      const parsed = parseApiFieldErrors(err);
      if (!form.use_catalog && parsed.service_id) {
        parsed.custom_service_name =
          parsed.custom_service_name || parsed.service_id;
        delete parsed.service_id;
      }
      setFieldErrors(parsed);
      showError(
        "Update failed",
        getApiErrorMessage(err, "Please try again.", parsed),
      );
    },
  });

  const deleteMutation = useDeleteVendorPrivateBooking({
    onSuccess: () => {
      success("Booking deleted", "Private booking removed from your calendar.");
      if (editingId) {
        setEditingId(null);
        setForm(emptyForm());
      }
    },
    onError: (err) => {
      showError(
        "Delete failed",
        err?.response?.data?.message || "Could not delete this booking.",
      );
    },
    onSettled: () => setDeletingId(null),
  });

  const importMutation = useImportVendorPrivateBookings({
    onSuccess: (res) => {
      const data = res?.data || {};
      setImportResult(data);
      const created = data.created ?? 0;
      const failed = data.failed ?? 0;
      if (created > 0) {
        success(
          "Import complete",
          failed > 0
            ? `${created} booking(s) added; ${failed} row(s) need fixes.`
            : `${created} booking(s) added to your calendar.`,
        );
        setImportFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        showError(
          "Nothing imported",
          res?.message || "Fix the row errors below and try again.",
        );
      }
    },
    onError: (err) => {
      setImportResult(err?.response?.data?.data || null);
      showError(
        "Import failed",
        err?.response?.data?.message || "Could not import this file.",
      );
    },
  });

  useEffect(() => {
    if (!form.use_catalog || !form.package_id) return;
    const pkg = allPackages.find((p) => p.id === form.package_id);
    if (!pkg) return;
    setForm((prev) => ({
      ...prev,
      subtotal_amount: prev.subtotal_amount || String(pkg.base_price ?? ""),
      duration_hours: prev.duration_hours || String(pkg.duration_hours ?? ""),
    }));
  }, [form.package_id, form.use_catalog, allPackages]);

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "service_id") {
        next.package_id = "";
      }
      if (field === "use_catalog" && value) {
        next.custom_service_name = "";
      }
      if (field === "use_catalog" && !value) {
        next.service_id = "";
        next.package_id = "";
      }
      return next;
    });
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        if (field === "use_catalog") {
          delete next.service_id;
          delete next.custom_service_name;
        }
        return next;
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!form.guest_name.trim()) {
      errors.guest_name = "Client name is required.";
    }
    if (form.guest_email && !/\S+@\S+\.\S+/.test(form.guest_email)) {
      errors.guest_email = "Enter a valid email address.";
    }
    if (!form.event_date) {
      errors.event_date = "Event date is required.";
    }
    if (form.use_catalog) {
      if (!form.service_id) {
        errors.service_id =
          "Select a service from your catalog or switch to custom entry.";
      }
    } else {
      if (!form.custom_service_name.trim()) {
        errors.custom_service_name = "Enter a name for this booking.";
      }
      if (form.subtotal_amount === "" || form.subtotal_amount == null) {
        errors.subtotal_amount = "Amount is required for custom entries.";
      }
    }
    if (form.discount_type) {
      const discountValueNum = parseFloat(form.discount_value);
      if (
        form.discount_value === "" ||
        form.discount_value == null ||
        Number.isNaN(discountValueNum)
      ) {
        errors.discount_value = "Enter a discount value.";
      } else if (discountValueNum <= 0) {
        errors.discount_value = "Discount must be greater than zero.";
      } else if (form.discount_type === "percent" && discountValueNum > 100) {
        errors.discount_value = "Percent discount cannot exceed 100.";
      } else if (form.discount_type === "fixed") {
        const amountNum = Number(form.subtotal_amount);
        if (Number.isFinite(amountNum) && discountValueNum > amountNum) {
          errors.discount_value = "Discount cannot exceed the service amount.";
        }
      }
    }
    if (form.start_time && form.end_time && form.end_time <= form.start_time) {
      errors.end_time = "End time must be after start time.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildPayload = () => {
    const payload = { ...form };
    delete payload.use_catalog;

    if (form.use_catalog) {
      delete payload.custom_service_name;
    } else {
      delete payload.service_id;
      delete payload.package_id;
      payload.custom_service_name = form.custom_service_name.trim();
      payload.custom_package_name = null;
    }

    ["duration_hours", "guest_count", "subtotal_amount", "amount_paid"].forEach(
      (key) => {
        if (payload[key] === "" || payload[key] == null) {
          delete payload[key];
        } else {
          payload[key] = Number(payload[key]);
        }
      },
    );

    const amountForDiscount = Number(form.subtotal_amount) || 0;
    if (
      form.discount_type &&
      form.discount_value !== "" &&
      form.discount_value != null
    ) {
      payload.discount_type = form.discount_type;
      payload.discount_value = Number(form.discount_value);
      payload.discount_amount = resolveDiscountAmount(
        amountForDiscount,
        form.discount_type,
        form.discount_value,
      );
    } else {
      payload.discount_type = null;
      payload.discount_value = null;
      payload.discount_amount = 0;
    }

    return payload;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showError(
        "Please fix the form",
        "Some fields need your attention before saving.",
      );
      return;
    }
    const payload = buildPayload();
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const startEdit = (booking) => {
    const useCatalog = !booking.custom_service_name && !!booking.service_id;
    const customName = booking.custom_service_name
      ? booking.custom_package_name
        ? `${booking.custom_service_name} — ${booking.custom_package_name}`
        : booking.custom_service_name
      : "";
    setEditingId(booking.id);
    setFieldErrors({});
    setForm({
      guest_name: booking.guest_name || "",
      guest_email: booking.guest_email || "",
      guest_phone: booking.guest_phone || "",
      manual_title: booking.manual_title || "",
      use_catalog: useCatalog,
      service_id: booking.service_id || "",
      package_id: booking.package_id || "",
      custom_service_name: customName,
      event_date:
        booking.event_date?.slice?.(0, 10) || booking.event_date || "",
      start_time:
        booking.start_time?.slice?.(11, 16) ||
        booking.start_time?.slice?.(0, 5) ||
        "10:00",
      end_time:
        booking.end_time?.slice?.(11, 16) ||
        booking.end_time?.slice?.(0, 5) ||
        "12:00",
      duration_hours:
        booking.duration_hours != null ? String(booking.duration_hours) : "",
      guest_count:
        booking.guest_count != null ? String(booking.guest_count) : "",
      event_type: booking.event_type || "Private",
      event_location: booking.event_location || "",
      event_address: booking.event_address || "",
      special_requirements: booking.special_requirements || "",
      vendor_notes: booking.vendor_notes || "",
      subtotal_amount:
        booking.subtotal_amount != null ? String(booking.subtotal_amount) : "",
      discount_type:
        booking.discount_type === "fixed" || booking.discount_type === "percent"
          ? booking.discount_type
          : Number(booking.discount_amount) > 0
            ? "fixed"
            : "",
      discount_value:
        booking.discount_type === "fixed" || booking.discount_type === "percent"
          ? booking.discount_value != null
            ? String(booking.discount_value)
            : ""
          : Number(booking.discount_amount) > 0
            ? String(booking.discount_amount)
            : "",
      amount_paid:
        booking.amount_paid != null ? String(booking.amount_paid) : "",
      status: booking.status || "confirmed",
    });
    setActiveTab("create");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFieldErrors({});
  };

  const bookingHasLinkedDocs = (booking) =>
    Boolean(
      booking?.has_invoice ||
      booking?.has_contract ||
      booking?.primary_invoice_id ||
      booking?.primary_contract_id ||
      (booking?.invoices && booking.invoices.length > 0) ||
      (booking?.contracts && booking.contracts.length > 0),
    );

  const handleDelete = (booking) => {
    if (bookingHasLinkedDocs(booking)) {
      showError(
        null,
        "This booking has a linked invoice or contract. Open View to manage those instead of deleting.",
      );
      return;
    }
    const label =
      booking.manual_title || booking.guest_name || booking.booking_reference;
    if (
      !window.confirm(
        `Delete private booking "${label}"? This cannot be undone.`,
      )
    ) {
      return;
    }
    setDeletingId(booking.id);
    deleteMutation.mutate(booking.id);
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const res = await vendorPrivateBookingApi.downloadImportTemplate();
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "private-bookings-import-template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      success(
        null,
        "Template downloaded. Fill it in Excel or Sheets, then Save As CSV.",
      );
    } catch (err) {
      showError(
        null,
        err?.response?.data?.message || "Could not download the template.",
      );
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleImportSubmit = (e) => {
    e.preventDefault();
    if (!importFile) {
      showError(
        "Choose a file",
        "Select the CSV you filled from the template.",
      );
      return;
    }
    setImportResult(null);
    importMutation.mutate(importFile);
  };

  const columns = useMemo(
    () => [
      {
        key: "manual_title",
        label: "Booking",
        sortable: true,
        mobilePrimary: true,
        width: "w-56",
        exportValue: (b) =>
          b.manual_title || b.guest_name || b.booking_reference || "",
        render: (b) => (
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-semibold text-ink truncate">
                {b.manual_title || b.guest_name || "Private client"}
              </span>
              <ChannelBadge channel="external" />
            </div>
            <span className="font-mono text-[11px] text-ink-tertiary tabular-nums">
              #{b.booking_reference}
            </span>
          </div>
        ),
      },
      {
        key: "guest_name",
        label: "Guest",
        sortable: true,
        width: "w-52",
        exportValue: (b) =>
          [b.guest_name, b.guest_email].filter(Boolean).join(" · ") || "",
        render: (b) => (
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 ring-2 ring-gray-200">
              <User className="h-4 w-4 text-ink-secondary" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-ink">
                {b.guest_name || "—"}
              </p>
              <p className="truncate text-xs text-ink-tertiary">
                {b.guest_email || b.guest_phone || "No contact"}
              </p>
            </div>
          </div>
        ),
      },
      {
        key: "event_date",
        label: "Event",
        sortable: true,
        type: "date",
        width: "w-40",
        exportValue: (b) =>
          `${formatDate(b.event_date)}${b.start_time ? ` ${formatTime(b.start_time)}` : ""}`,
        render: (b) => (
          <div className="flex items-center gap-2 min-w-0">
            <Calendar className="h-4 w-4 shrink-0 text-ink-tertiary" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink">
                {formatDate(b.event_date)}
              </p>
              <p className="inline-flex items-center gap-1 text-xs text-ink-tertiary">
                <Clock className="h-3 w-3" />
                {b.start_time ? formatTime(b.start_time) : "TBD"}
              </p>
            </div>
          </div>
        ),
      },
      {
        key: "service",
        label: "Service",
        sortable: false,
        width: "w-44",
        exportValue: (b) =>
          b.custom_service_name || b.vendor_service?.service_name || "",
        render: (b) => (
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-ink">
              {b.custom_service_name || b.vendor_service?.service_name || "—"}
            </p>
            {(b.has_invoice ||
              b.primary_invoice_id ||
              b.has_contract ||
              b.primary_contract_id) && (
              <div className="mt-1 flex flex-wrap gap-1">
                {(b.has_invoice || b.primary_invoice_id) && (
                  <span className="inline-flex items-center gap-0.5 rounded border border-sky-200 bg-sky-50 px-1 py-0.5 text-[10px] font-medium text-sky-800">
                    <FileText className="h-2.5 w-2.5" />
                    Invoice
                  </span>
                )}
                {(b.has_contract || b.primary_contract_id) && (
                  <span className="inline-flex items-center gap-0.5 rounded border border-violet-200 bg-violet-50 px-1 py-0.5 text-[10px] font-medium text-violet-800">
                    <FileSignature className="h-2.5 w-2.5" />
                    Contract
                  </span>
                )}
              </div>
            )}
          </div>
        ),
      },
      {
        key: "vendor_payout_amount",
        label: "Amount",
        sortable: true,
        width: "w-28",
        type: "currency",
        exportValue: (b) => privateBookingNetAmount(b).toFixed(2),
        render: (b) => {
          const net = privateBookingNetAmount(b);
          const discount = Number(b.discount_amount) || 0;
          return (
            <div className="text-right tabular-nums">
              <p className="text-sm font-semibold text-ink">
                ${net.toFixed(2)}
              </p>
              {discount > 0 ? (
                <p className="text-[11px] font-medium text-emerald-700">
                  −${discount.toFixed(2)}
                </p>
              ) : null}
            </div>
          );
        },
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: "w-32",
        type: "status",
        render: (b) => getStatusBadge(b.status),
      },
    ],
    [],
  );

  const tableActions = useMemo(
    () => [
      {
        label: "View",
        icon: Eye,
        onClick: (b) => navigate(`/vendor/bookings/${b.id}`),
      },
      {
        label: "Edit",
        icon: Pencil,
        onClick: (b) => startEdit(b),
      },
      {
        label: (b) => (deletingId === b.id ? "Deleting…" : "Delete"),
        icon: Trash2,
        variant: "danger",
        show: (b) => !bookingHasLinkedDocs(b),
        onClick: (b) => handleDelete(b),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- startEdit / handleDelete stable enough for this page
    [navigate, deletingId],
  );

  const handleTableSearch = (data, term) => {
    if (!term) return data;
    const q = term.toLowerCase();
    return data.filter(
      (b) =>
        b.booking_reference?.toLowerCase().includes(q) ||
        b.guest_name?.toLowerCase().includes(q) ||
        b.guest_email?.toLowerCase().includes(q) ||
        b.guest_phone?.toLowerCase().includes(q) ||
        b.manual_title?.toLowerCase().includes(q) ||
        b.custom_service_name?.toLowerCase().includes(q) ||
        b.vendor_service?.service_name?.toLowerCase().includes(q) ||
        b.status?.toLowerCase().includes(q),
    );
  };

  const summaryCards = [
    {
      label: "Total bookings",
      value: stats.total_bookings ?? bookings.length ?? 0,
      hint: "All external / private jobs",
      Icon: BarChart3,
      accent: "text-ink",
      iconWrap: "border-border bg-surface-muted text-ink",
    },
    {
      label: "Upcoming",
      value: upcomingCount,
      hint: "Pending · confirmed · in progress",
      Icon: Clock,
      accent: "text-amber-600",
      iconWrap: "border-amber-200/80 bg-amber-50/50 text-amber-600",
    },
    {
      label: "Completed",
      value: stats.completed_bookings ?? 0,
      hint: "Finished external events",
      Icon: CheckCircle2,
      accent: "text-emerald-600",
      iconWrap: "border-emerald-200/80 bg-emerald-50/50 text-emerald-600",
    },
    {
      label: "Completed revenue",
      value: `$${Number(stats.total_revenue || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      hint: "From completed private jobs",
      Icon: DollarSign,
      accent: "text-ink",
      iconWrap: "border-emerald-200/80 bg-emerald-50/50 text-emerald-600",
    },
  ];

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Private bookings"
        subtitle="External — log your own clients’ jobs. They show on your calendar and can generate invoices, but stay outside marketplace accounting."
        actions={
          activeTab === "list" ? (
            <>
              <ChannelBadge channel="external" />
              <Button
                onClick={() => {
                  cancelEdit();
                  setActiveTab("create");
                }}
                className="rounded-md"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                New private booking
              </Button>
            </>
          ) : null
        }
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.privateBookings.title}
        steps={VENDOR_PAGE_GUIDES.privateBookings.steps}
        icon={ListChecks}
      />

      {activeTab === "list" ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {summaryCards.map((card) => {
            const Icon = card.Icon;
            return (
              <div key={card.label} className="product-panel p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink-tertiary">
                      {card.label}
                    </p>
                    <p
                      className={`mt-1 text-2xl font-bold tracking-tight tabular-nums ${card.accent}`}
                    >
                      {card.value}
                    </p>
                    <p className="mt-1 text-[11px] text-ink-tertiary">
                      {card.hint}
                    </p>
                  </div>
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${card.iconWrap}`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      <div className="product-panel overflow-hidden">
        <div className="border-b border-border px-4 py-3 sm:px-5">
          <div className="inline-flex rounded-xl bg-gray-100 p-1 border border-border">
            {[
              { key: "list", label: "My private bookings", icon: Lock },
              {
                key: "create",
                label: editingId ? "Edit booking" : "Create booking",
                icon: CalendarPlus,
              },
              { key: "import", label: "Import CSV", icon: Upload },
            ].map((tab) => {
              const TabIcon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => {
                    if (tab.key !== "create") {
                      cancelEdit();
                    }
                    if (tab.key === "import") {
                      setImportResult(null);
                    }
                    setActiveTab(tab.key);
                  }}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${isActive ? "bg-surface text-ink shadow-sm" : "text-ink-secondary hover:text-gray-900"}`}
                >
                  <TabIcon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {activeTab === "list" && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {[
                  { value: "", label: "All", count: stats.total_bookings || 0 },
                  {
                    value: "pending",
                    label: "Pending",
                    count: stats.pending_bookings || 0,
                  },
                  {
                    value: "confirmed",
                    label: "Confirmed",
                    count: stats.confirmed_bookings || 0,
                  },
                  {
                    value: "in_progress",
                    label: "In progress",
                    count: stats.in_progress_bookings || 0,
                  },
                  {
                    value: "completed",
                    label: "Completed",
                    count: stats.completed_bookings || 0,
                  },
                  {
                    value: "cancelled",
                    label: "Cancelled",
                    count: stats.cancelled_bookings || 0,
                  },
                ].map((tab) => (
                  <button
                    key={tab.value || "all"}
                    type="button"
                    onClick={() => setStatusFilter(tab.value)}
                    className={`product-chip ${statusFilter === tab.value ? "is-active" : ""}`}
                  >
                    {tab.label}{" "}
                    <span className="tabular-nums opacity-70">
                      ({tab.count})
                    </span>
                  </button>
                ))}
              </div>

              {!isLoading && bookings.length === 0 && !statusFilter ? (
                <div className="py-12 text-center px-4">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg border border-border bg-surface-muted text-gray-400">
                    <CalendarPlus className="w-7 h-7" />
                  </div>
                  <h3 className="text-base font-semibold text-ink">
                    No private bookings yet
                  </h3>
                  <p className="mt-1.5 text-sm text-ink-tertiary max-w-sm mx-auto leading-relaxed">
                    Log off-platform jobs here so they appear on your calendar
                    and can be invoiced.
                  </p>
                  <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                    <Button
                      className="rounded-md"
                      onClick={() => setActiveTab("create")}
                    >
                      <Plus className="w-4 h-4 mr-1.5" />
                      Create your first private booking
                    </Button>
                    <Button
                      variant="outline"
                      className="rounded-md"
                      onClick={() => setActiveTab("import")}
                    >
                      <Upload className="w-4 h-4 mr-1.5" />
                      Import CSV
                    </Button>
                  </div>
                </div>
              ) : (
                <DataTable
                  data={bookings}
                  columns={columns}
                  actions={tableActions}
                  loading={isLoading}
                  searchable
                  filterable={false}
                  sortable
                  exportable
                  exportFileName="private-bookings"
                  onSearch={handleTableSearch}
                  onRowClick={(b) => navigate(`/vendor/bookings/${b.id}`)}
                  searchPlaceholder="Search by guest, reference, service…"
                  emptyMessage={
                    statusFilter
                      ? `No ${statusFilter.replace("_", " ")} private bookings.`
                      : "No private bookings found."
                  }
                  pageSize={15}
                  showPagination
                  mobileCardTitleKey="manual_title"
                  className="!border-0 !shadow-none"
                />
              )}
            </div>
          )}

          {activeTab === "import" && (
            <div className="space-y-6 max-w-3xl">
              <div>
                <h2 className="text-base font-semibold text-ink">
                  Import private bookings
                </h2>
                <p className="mt-1 text-sm text-ink-secondary leading-relaxed">
                  Download the CSV template, fill one row per booking in Excel
                  or Google Sheets, then Save As → CSV UTF-8 and upload it here.
                  Up to 100 rows per file.
                </p>
              </div>

              <div className="rounded-xl border border-border bg-surface-muted/40 p-4 sm:p-5 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-ink-tertiary">
                    <FileSpreadsheet className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-ink">
                      1. Download the format
                    </p>
                    <p className="mt-0.5 text-xs text-ink-tertiary">
                      Required columns: guest_name, service_name, event_date,
                      start_time, end_time. Use your catalog service name when
                      it matches; otherwise enter a custom name and
                      subtotal_amount.
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="mt-3 rounded-md"
                      onClick={handleDownloadTemplate}
                      loading={downloadingTemplate}
                      icon={Download}
                    >
                      Download CSV template
                    </Button>
                  </div>
                </div>
              </div>

              <form
                onSubmit={handleImportSubmit}
                className="rounded-xl border border-border bg-surface-muted/40 p-4 sm:p-5 space-y-4"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-ink-tertiary">
                    <Upload className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1 space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-ink">
                        2. Upload your CSV
                      </p>
                      <p className="mt-0.5 text-xs text-ink-tertiary">
                        Binary Excel files (.xlsx) are not accepted — export as
                        CSV first.
                      </p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      className="block w-full text-sm text-ink-secondary file:mr-3 file:rounded-md file:border-0 file:bg-ink file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-ink-on-dark hover:file:opacity-90"
                      onChange={(e) => {
                        setImportFile(e.target.files?.[0] || null);
                        setImportResult(null);
                      }}
                    />
                    {importFile && (
                      <p className="text-xs text-ink-secondary">
                        Selected:{" "}
                        <span className="font-medium text-ink">
                          {importFile.name}
                        </span>
                      </p>
                    )}
                    <Button
                      type="submit"
                      className="rounded-md"
                      loading={importMutation.isPending}
                      disabled={!importFile}
                      icon={Upload}
                    >
                      Import bookings
                    </Button>
                  </div>
                </div>
              </form>

              {importResult && (
                <div className="space-y-3">
                  <div
                    className={`rounded-xl border px-4 py-3 text-sm ${
                      (importResult.created ?? 0) > 0
                        ? "border-emerald-200 bg-emerald-50/80 text-emerald-900"
                        : "border-amber-200 bg-amber-50/80 text-amber-900"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {(importResult.created ?? 0) > 0 ? (
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                      ) : (
                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      )}
                      <div>
                        <p className="font-medium">
                          {importResult.created ?? 0} created ·{" "}
                          {importResult.failed ?? 0} failed ·{" "}
                          {importResult.total_rows ?? 0} rows
                        </p>
                        {(importResult.bookings || []).length > 0 && (
                          <ul className="mt-1.5 space-y-0.5 text-xs opacity-90">
                            {importResult.bookings.slice(0, 8).map((b) => (
                              <li key={b.id}>
                                #{b.booking_reference} — {b.guest_name} (
                                {b.event_date})
                              </li>
                            ))}
                            {(importResult.bookings || []).length > 8 && (
                              <li>
                                +{(importResult.bookings || []).length - 8} more
                              </li>
                            )}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>

                  {(importResult.errors || []).length > 0 && (
                    <div className="rounded-xl border border-red-200 bg-red-50/60 overflow-hidden">
                      <div className="border-b border-red-200 px-4 py-2.5">
                        <p className="text-sm font-semibold text-red-800">
                          Row errors
                        </p>
                      </div>
                      <ul className="divide-y divide-red-100 max-h-64 overflow-y-auto">
                        {importResult.errors.map((err, idx) => (
                          <li
                            key={`${err.row}-${idx}`}
                            className="px-4 py-2.5 text-sm text-red-800"
                          >
                            <span className="font-medium tabular-nums">
                              {err.row > 0 ? `Row ${err.row}` : "File"}
                            </span>
                            <span className="text-red-700">
                              {" "}
                              — {err.message}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === "create" && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {Object.keys(fieldErrors).length > 0 && (
                <div className="rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-700">
                  <p className="font-medium">Please fix the following:</p>
                  <ul className="mt-1 list-disc list-inside space-y-0.5">
                    {Object.values(fieldErrors).map((msg, index) => (
                      <li key={index}>{msg}</li>
                    ))}
                  </ul>
                </div>
              )}

              <section className="rounded-xl border border-border bg-surface-muted/40 p-4 sm:p-5 space-y-4">
                <div>
                  <h2 className="text-sm font-semibold text-ink">
                    Client details
                  </h2>
                  <p className="text-xs text-ink-tertiary mt-0.5">
                    No platform account needed — enter contact info only.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Client name *"
                    value={form.guest_name}
                    onChange={(e) => handleChange("guest_name", e.target.value)}
                    placeholder="Jane Smith"
                    error={fieldErrors.guest_name}
                  />
                  <Input
                    label="Email"
                    type="email"
                    value={form.guest_email}
                    onChange={(e) =>
                      handleChange("guest_email", e.target.value)
                    }
                    placeholder="client@email.com"
                    error={fieldErrors.guest_email}
                  />
                  <Input
                    label="Phone"
                    value={form.guest_phone}
                    onChange={(e) =>
                      handleChange("guest_phone", e.target.value)
                    }
                    placeholder="+1 555 000 0000"
                    error={fieldErrors.guest_phone}
                  />
                  <Input
                    label="Title / label (optional)"
                    value={form.manual_title}
                    onChange={(e) =>
                      handleChange("manual_title", e.target.value)
                    }
                    placeholder="e.g. Smith wedding — photo booth"
                    error={fieldErrors.manual_title}
                  />
                </div>
              </section>

              <section className="rounded-xl border border-border bg-surface-muted/40 p-4 sm:p-5 space-y-4">
                <div>
                  <h2 className="text-sm font-semibold text-ink">
                    Service &amp; package
                  </h2>
                </div>
                <div className="inline-flex rounded-xl bg-gray-100 p-1 border border-border">
                  <button
                    type="button"
                    onClick={() => handleChange("use_catalog", true)}
                    className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all ${form.use_catalog ? "bg-surface text-ink shadow-sm" : "text-ink-secondary"}`}
                  >
                    From my catalog
                  </button>
                  <button
                    type="button"
                    onClick={() => handleChange("use_catalog", false)}
                    className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all ${!form.use_catalog ? "bg-surface text-ink shadow-sm" : "text-ink-secondary"}`}
                  >
                    Custom entry
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {form.use_catalog ? (
                    <>
                      <CustomSelect
                        label="Service *"
                        options={serviceOptions}
                        value={form.service_id}
                        onChange={(v) => handleChange("service_id", v)}
                        placeholder="Select service"
                        error={fieldErrors.service_id}
                      />
                      <CustomSelect
                        label="Package (optional)"
                        options={packageOptions}
                        value={form.package_id}
                        onChange={(v) => handleChange("package_id", v)}
                        placeholder={
                          form.service_id
                            ? "Select package"
                            : "Choose a service first"
                        }
                        disabled={!form.service_id}
                        error={fieldErrors.package_id}
                      />
                    </>
                  ) : (
                    <Input
                      label="Name *"
                      value={form.custom_service_name}
                      onChange={(e) =>
                        handleChange("custom_service_name", e.target.value)
                      }
                      placeholder="e.g. Catering booth — 4 hour package"
                      error={fieldErrors.custom_service_name}
                    />
                  )}
                  <Input
                    label={`Amount ($)${!form.use_catalog ? " *" : ""}`}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.subtotal_amount}
                    onChange={(e) =>
                      handleChange("subtotal_amount", e.target.value)
                    }
                    placeholder={
                      form.use_catalog
                        ? "Auto-filled from package"
                        : "Enter amount"
                    }
                    error={fieldErrors.subtotal_amount}
                  />
                  <Input
                    label="Amount paid ($)"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.amount_paid}
                    onChange={(e) =>
                      handleChange("amount_paid", e.target.value)
                    }
                    placeholder="0"
                    error={fieldErrors.amount_paid}
                  />
                </div>
                <div className="space-y-3 pt-1">
                  <div>
                    <p className="text-sm font-semibold text-ink mb-1">
                      Discount (optional)
                    </p>
                    <p className="text-xs text-ink-tertiary mb-3">
                      Choose a fixed dollar amount or a percent of the service
                      amount. Linked invoices stay in sync.
                    </p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {[
                        { value: "", label: "No discount" },
                        { value: "fixed", label: "Fixed $" },
                        { value: "percent", label: "Percent %" },
                      ].map((opt) => (
                        <button
                          key={opt.value || "none"}
                          type="button"
                          onClick={() => {
                            setForm((prev) => ({
                              ...prev,
                              discount_type: opt.value,
                              discount_value: opt.value
                                ? prev.discount_value
                                : "",
                            }));
                            if (
                              fieldErrors.discount_value ||
                              fieldErrors.discount_type
                            ) {
                              setFieldErrors((prev) => {
                                const next = { ...prev };
                                delete next.discount_value;
                                delete next.discount_type;
                                return next;
                              });
                            }
                          }}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                            form.discount_type === opt.value
                              ? "bg-gray-900 text-white border-gray-900"
                              : "bg-surface text-ink-secondary border-border hover:bg-gray-100"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {form.discount_type && (
                      <Input
                        label={
                          form.discount_type === "percent"
                            ? "Discount percent"
                            : "Discount amount ($)"
                        }
                        type="number"
                        min="0"
                        max={
                          form.discount_type === "percent" ? "100" : undefined
                        }
                        step="0.01"
                        value={form.discount_value}
                        onChange={(e) =>
                          handleChange("discount_value", e.target.value)
                        }
                        placeholder={
                          form.discount_type === "percent"
                            ? "e.g. 10"
                            : "e.g. 50.00"
                        }
                        error={
                          fieldErrors.discount_value ||
                          fieldErrors.discount_type
                        }
                      />
                    )}
                    {(() => {
                      const amount = Number(form.subtotal_amount);
                      const discount = resolveDiscountAmount(
                        amount,
                        form.discount_type,
                        form.discount_value,
                      );
                      if (
                        !Number.isFinite(amount) ||
                        amount < 0 ||
                        discount <= 0
                      )
                        return null;
                      const due = Math.max(
                        0,
                        Math.round((amount - discount) * 100) / 100,
                      );
                      return (
                        <p className="text-sm text-ink-secondary mt-2">
                          Discount applied:{" "}
                          <span className="font-medium text-emerald-700 tabular-nums">
                            −${discount.toFixed(2)}
                          </span>
                          {" · "}
                          Amount due:{" "}
                          <span className="font-semibold text-ink tabular-nums">
                            ${due.toFixed(2)}
                          </span>
                        </p>
                      );
                    })()}
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-border bg-surface-muted/40 p-4 sm:p-5 space-y-4">
                <h2 className="text-sm font-semibold text-ink">
                  Event schedule
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-ink-secondary mb-1">
                      Event date *
                    </label>
                    <DatePicker
                      value={form.event_date}
                      onChange={(d) => handleChange("event_date", d)}
                      placeholder="Select date"
                      error={fieldErrors.event_date}
                    />
                  </div>
                  <Input
                    label="Start time *"
                    type="time"
                    value={form.start_time}
                    onChange={(e) => handleChange("start_time", e.target.value)}
                    error={fieldErrors.start_time}
                  />
                  <Input
                    label="End time *"
                    type="time"
                    value={form.end_time}
                    onChange={(e) => handleChange("end_time", e.target.value)}
                    error={fieldErrors.end_time}
                  />
                  <Input
                    label="Duration (hours)"
                    type="number"
                    min="0.25"
                    step="0.25"
                    value={form.duration_hours}
                    onChange={(e) =>
                      handleChange("duration_hours", e.target.value)
                    }
                    error={fieldErrors.duration_hours}
                  />
                  <Input
                    label="Guest count"
                    type="number"
                    min="1"
                    value={form.guest_count}
                    onChange={(e) =>
                      handleChange("guest_count", e.target.value)
                    }
                    error={fieldErrors.guest_count}
                  />
                  <CustomSelect
                    label="Status"
                    options={STATUS_OPTIONS}
                    value={form.status}
                    onChange={(v) => handleChange("status", v)}
                    error={fieldErrors.status}
                  />
                </div>
              </section>

              <section className="rounded-xl border border-border bg-surface-muted/40 p-4 sm:p-5 space-y-4">
                <h2 className="text-sm font-semibold text-ink">
                  Location &amp; notes
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Event location"
                    value={form.event_location}
                    onChange={(e) =>
                      handleChange("event_location", e.target.value)
                    }
                    error={fieldErrors.event_location}
                  />
                  <Input
                    label="Address"
                    value={form.event_address}
                    onChange={(e) =>
                      handleChange("event_address", e.target.value)
                    }
                    error={fieldErrors.event_address}
                  />
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-ink-secondary mb-1">
                      Special requirements
                    </label>
                    <textarea
                      className={`w-full border rounded-xl px-3 py-1.5 text-xs bg-surface ${fieldErrors.special_requirements ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"}`}
                      rows={2}
                      value={form.special_requirements}
                      onChange={(e) =>
                        handleChange("special_requirements", e.target.value)
                      }
                    />
                    {fieldErrors.special_requirements && (
                      <p className="mt-1 text-sm text-red-600">
                        {fieldErrors.special_requirements}
                      </p>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-ink-secondary mb-1">
                      Your notes (private)
                    </label>
                    <textarea
                      className={`w-full border rounded-xl px-3 py-1.5 text-xs bg-surface ${fieldErrors.vendor_notes ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"}`}
                      rows={2}
                      value={form.vendor_notes}
                      onChange={(e) =>
                        handleChange("vendor_notes", e.target.value)
                      }
                    />
                    {fieldErrors.vendor_notes && (
                      <p className="mt-1 text-sm text-red-600">
                        {fieldErrors.vendor_notes}
                      </p>
                    )}
                  </div>
                </div>
              </section>

              <div className="flex flex-wrap gap-3 justify-end pt-2 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-md"
                  onClick={() => {
                    cancelEdit();
                    setActiveTab("list");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="rounded-md"
                  loading={createMutation.isPending || updateMutation.isPending}
                >
                  {editingId ? "Save changes" : "Create private booking"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorPrivateBookings;
